/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import Downshift, { DownshiftProps } from 'downshift';
import { debounce, throttle } from 'lodash';
import { useTranslation } from 'next-i18next';
import {
    Ref,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { SearchTermModel } from '@toucan/common/services/search/types';

import {
    FUZZY_SEARCH_ENABLED_FLAG,
    useFeatureFlag,
} from '@toucan/client/global/feature-flag';
import { MarketContext } from '@toucan/client/global/market';

import {
    getModelBySearchTerm,
    getModelSearchSuggestionsAdvanced,
    getModelSuggestions,
} from '@toucan/client/core/search';

import {
    TypeaheadHandles,
    TypeaheadSuggestion,
} from '@toucan/client/shared/components/typeahead';

type onStateChange = DownshiftProps<TypeaheadSuggestion>['onStateChange'];

type Item = 'selectedItem' | 'searchValue';
export type SuggestionsSetFieldValue = <T extends Item>(
    key: T,
    value: T extends 'selectedItem' ? SearchTermModel | undefined : string
) => void;

type QueuedAction = () => void;

type UseModelSuggestionsReturn = {
    /**
     * If suggestions are being loaded
     */
    loading: boolean;

    /**
     * An error caused from getting the search suggestions
     */
    error: string;

    /**
     * A ref which should be applied to the Typeahead component
     */
    typeaheadRef: Ref<TypeaheadHandles>;

    /**
     * A handler to be passed to the onStateChange prop of the Typeahead component
     */
    onStateChange: onStateChange;

    /**
     * A handler to be passed to the onSearchClick prop of the Typeahead component
     */
    onSearchClick: (searchTerm: string) => void;

    /**
     * A handler to be passed to the onInputValueChange prop of the Typeahead component
     */
    onInputValueChange: (searchTerm: string) => void;

    /**
     * If the Typeahead component should be read only
     */
    readOnly: boolean;

    /**
     * The search suggestions to show in the Typeahead component
     */
    searchSuggestions: TypeaheadSuggestion[];

    /**
     * This fn lets you change the function used to update the searchValue
     * & selectedItem values. Handy for when using with Formik, see
     * QuoteBuyItemSelect component for an example
     */
    updateSetFieldValue: (fn: SuggestionsSetFieldValue) => void;

    /**
     * Is the search suggestions hook performing a full search
     */
    isFullSearch: boolean;
};

type UseModelSuggestionsArgs = {
    /**
     * An initial search value
     */
    initialSearchValue?: string;

    /**
     * A function that will be called to set values for
     * searchValue & selectedItem, so they can be stored outside the handler
     */
    setFieldValue?: SuggestionsSetFieldValue;

    /**
     * A callback that's ran after the user clicks a suggestion
     * and the request to get the model info is returned
     */
    selectedItemCallback?: (model: SearchTermModel) => void;

    /**
     * A callback that runs when an item is highlighted, being provided
     * the index of the highlighted item
     *
     * @param index
     */
    highlightItemCallback?: (index: number) => void;

    /**
     * A callback that is ran when the input changes
     */
    onInputChangeCallback?: () => void;

    /**
     * Only handle suggestions, not the selection of suggestions.
     * Relies on the user implementing their own change handling for Typeahead
     */
    onlySuggestions?: boolean;

    /**
     * Automatically replace search results with spellcheck suggestion
     * for misspelled words with no results.
     */
    autoCorrectSearchTermSpelling?: boolean;
};

/**
 * A hook to setup model search suggestions in the typeahead component
 */
export const useModelSuggestions = ({
    initialSearchValue,
    setFieldValue,
    selectedItemCallback,
    highlightItemCallback,
    onInputChangeCallback,
    onlySuggestions = false,
    autoCorrectSearchTermSpelling = false,
}: UseModelSuggestionsArgs = {}): UseModelSuggestionsReturn => {
    const { t } = useTranslation();
    const [searchSuggestions, setSearchSuggestions] = useState<
        TypeaheadSuggestion[]
    >([]);
    const [error, setError] = useState<string>('');
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [isFullSearch, setIsFullSearch] = useState<boolean>(false);
    const internalTypeaheadRef = useRef<TypeaheadHandles | null>(null);
    const actionQueue = useRef<QueuedAction[]>([]);
    const isFuzzySearchSuggestionsEnabled = Boolean(
        useFeatureFlag(FUZZY_SEARCH_ENABLED_FLAG)?.isEnabled
    );

    const getAutoCorrectedModelSearchSuggestionsAdvanced = async (
        searchTerm: string
    ): Promise<TypeaheadSuggestion[]> => {
        fetchSuggestionsDebounced.current.cancel();
        fetchSuggestionsThrottled.current.cancel();
        const { spellcheckSearchTerm, suggestionCount, suggestions } =
            await getModelSearchSuggestionsAdvanced(
                searchTerm,
                activeMarket.iso,
                activeMarket.code
            );

        if (
            autoCorrectSearchTermSpelling &&
            suggestionCount === 0 &&
            spellcheckSearchTerm
        ) {
            const { suggestions: autoCorrectedSuggestions } =
                await getModelSearchSuggestionsAdvanced(
                    spellcheckSearchTerm,
                    activeMarket.iso,
                    activeMarket.code
                );
            return autoCorrectedSuggestions;
        }
        return suggestions;
    };

    /**
     * Runs the queue of actions
     */
    const runActionQueue = () => {
        while (actionQueue.current.length > 0) {
            const action = actionQueue.current.shift();
            action && action();
        }
    };

    /**
     * Adds a function to the action queue
     *
     * @param fn The function to add to the queue
     */
    const addToActionQueue = (fn: QueuedAction) => {
        actionQueue.current.push(fn);
        internalTypeaheadRef.current && runActionQueue();
    };

    /**
     * If the current typeahead ref changes, act on the queue of actions that
     * have been set. Used for when an action is required after a batch of setState
     * calls could mean the ref is yet to be re-defined.
     */
    const typeaheadRef = useCallback((component: TypeaheadHandles) => {
        internalTypeaheadRef.current = component;
        runActionQueue();
    }, []);

    // current value will be the most recent id passed to fetch suggestions
    const fetchSuggestionsId = useRef('');
    const isMounted = useRef(false);

    const noop = () => {};
    const updateFieldValue = useRef<SuggestionsSetFieldValue>(
        setFieldValue || noop
    );
    const { activeMarket } = useContext(MarketContext);
    const genericErrorMessage = t('uh oh! something went wrong');

    const fetchSuggestions = async (inputValue: string, id: string) => {
        try {
            const newSuggestions = await getModelSuggestions(
                inputValue,
                activeMarket.iso,
                isFuzzySearchSuggestionsEnabled
            );

            isMounted.current && setError('');
            isMounted.current && setSearchSuggestions(newSuggestions);
        } catch (e) {
            console.error(e);
            isMounted.current && setError(genericErrorMessage);
        } finally {
            // If this is the most recently called function then setLoading to false
            if (fetchSuggestionsId.current === id) {
                isMounted.current && setLoading(false);
            }
        }
    };

    const fetchSuggestionsDebounced = useRef(debounce(fetchSuggestions, 500));
    const fetchSuggestionsThrottled = useRef(throttle(fetchSuggestions, 300));

    const onStateChange: onStateChange = async (changes) => {
        switch (changes.type) {
            case Downshift.stateChangeTypes.changeInput: {
                updateFieldValue.current('selectedItem', undefined);

                // Can't reproduce but very occasionally inputValue would be undefined
                const inputValue = !changes.inputValue
                    ? ''
                    : changes.inputValue;

                updateFieldValue.current('searchValue', inputValue);

                if (inputValue.trim() !== '') {
                    const fetchSuggestionsLimited =
                        inputValue.length < 5
                            ? fetchSuggestionsThrottled.current
                            : fetchSuggestionsDebounced.current;

                    setLoading(true);
                    setIsFullSearch(false);
                    const id = Date.now().toString();
                    fetchSuggestionsId.current = id;
                    fetchSuggestionsLimited(inputValue, id);
                } else {
                    setSearchSuggestions([]);
                }

                onInputChangeCallback && onInputChangeCallback();

                return;
            }

            case Downshift.stateChangeTypes.keyDownArrowDown:
            case Downshift.stateChangeTypes.keyDownArrowUp: {
                const index = changes.highlightedIndex;
                highlightItemCallback &&
                    highlightItemCallback(
                        typeof index === 'number' ? index : -1
                    );
                return;
            }

            case Downshift.stateChangeTypes.clickItem:
            case Downshift.stateChangeTypes.keyDownEnter: {
                // Bail if onlySuggestions is true
                if (onlySuggestions) {
                    return;
                }

                // Stops items being unselected when clicked twice
                if (changes.selectedItem === undefined) {
                    addToActionQueue(() =>
                        internalTypeaheadRef?.current?.close()
                    );
                    return;
                }

                const searchTerm =
                    changes.inputValue || changes.selectedItem?.term || '';

                updateFieldValue.current('searchValue', searchTerm);
                updateFieldValue.current('selectedItem', undefined);

                if (searchTerm) {
                    try {
                        const model = await getModelBySearchTerm(
                            searchTerm,
                            activeMarket.iso,
                            activeMarket.code
                        );
                        addToActionQueue(() =>
                            internalTypeaheadRef?.current?.close()
                        );
                        updateFieldValue.current('selectedItem', model);
                        selectedItemCallback && selectedItemCallback(model);
                    } catch (e) {
                        addToActionQueue(() =>
                            internalTypeaheadRef?.current?.close()
                        );
                        console.error(e);
                        setError(genericErrorMessage);
                    }
                }
                return;
            }

            default:
                return;
        }
    };

    async function onSearchClick(searchTerm: string) {
        setLoading(true);
        setReadOnly(true);
        setIsFullSearch(true);
        setSearchSuggestions([]);

        try {
            const suggestions =
                await getAutoCorrectedModelSearchSuggestionsAdvanced(
                    searchTerm
                );

            setSearchSuggestions(suggestions);

            addToActionQueue(() => {
                internalTypeaheadRef.current?.focus();
            });
        } catch (e) {
            console.error(e);
            setError(genericErrorMessage);
        } finally {
            setLoading(false);
            setReadOnly(false);
        }
    }

    useEffect(() => {
        if (setFieldValue) {
            updateFieldValue.current = setFieldValue;
        }
    }, [setFieldValue]);

    /**
     * Load suggestions if there is an initial search value
     */
    useEffect(() => {
        isMounted.current = true;
        if (initialSearchValue) {
            setLoading(true);
            const id = Date.now().toString();
            fetchSuggestionsId.current = id;
            fetchSuggestions(initialSearchValue, id);
        }

        return () => {
            isMounted.current = false;
        };
    }, []);

    return {
        error,
        loading,
        readOnly,
        typeaheadRef,
        onStateChange,
        onSearchClick,
        searchSuggestions,
        onInputValueChange: (inputValue: string) => {
            updateFieldValue.current('searchValue', inputValue);
        },
        updateSetFieldValue: (fn: SuggestionsSetFieldValue) => {
            updateFieldValue.current = fn;
        },
        isFullSearch,
    };
};
